import { useState, useRef } from "react";
import { 
  Download, 
  Printer,
  ChevronLeft,
  ChevronRight,
  Droplets,
  TrendingUp,
  Users,
  DollarSign,
  Target,
  Award,
  Rocket,
  Globe,
  CheckCircle2,
  Building2,
  Code,
  Package,
  Shield,
  Zap,
  BarChart3,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Hospital,
  Truck,
  Layers,
  Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";

// George Broughton Image
const GEORGE_IMAGE = "https://customer-assets.emergentagent.com/job_service-widgets/artifacts/n8vnfz06_Gemini_Generated_Image_ntzefxntzefxntze%20%281%29.png";
const STERILE_MATE_LOGO = "/logos/askisaac-b-monogram.svg";

export default function PitchDeckPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const deckRef = useRef(null);

  const handleDownload = () => {
    window.print();
  };

  const slides = [
    // Slide 1: Title
    {
      id: "title",
      content: (
        <div className="h-full flex flex-col items-center justify-center text-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-4 sm:p-8 md:p-12">
          <img src={STERILE_MATE_LOGO} alt="AskIsaac Inc" className="w-24 sm:w-36 md:w-48 h-auto mb-4 sm:mb-6 md:mb-8" />
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-4">AskIsaac Inc</h1>
          <p className="text-base sm:text-xl md:text-2xl text-blue-300 mb-4 sm:mb-6 md:mb-8">On-Demand Healthcare Operations</p>
          <div className="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mb-4 sm:mb-6 md:mb-8" />
          <p className="text-sm sm:text-lg md:text-xl text-slate-300">Investor Pitch Deck</p>
          <p className="text-xs sm:text-base md:text-lg text-slate-400 mt-2 sm:mt-4">January 2026</p>
          <div className="absolute bottom-2 sm:bottom-4 md:bottom-8 text-xs sm:text-sm text-slate-500">
            Confidential — For Approved Investors Only
          </div>
        </div>
      )
    },
    // Slide 2: The Problem
    {
      id: "problem",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-700 mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Target className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
            The Opportunity: Expand Your Network
          </h2>
          <div className="flex-1 space-y-4">
            <p className="text-sm sm:text-base md:text-lg text-slate-700">
              Healthcare facilities need to <strong className="text-blue-600">expand beyond their primary networks</strong>. Time-sensitive needs, 
              specialized contractors, and complex capital projects require more options and faster response times.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 text-center">
                <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">24/7</p>
                <p className="text-xs sm:text-sm text-slate-600">On-demand access to vetted vendors</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 text-center">
                <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">3+</p>
                <p className="text-xs sm:text-sm text-slate-600">Competitive quotes automated</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 text-center">
                <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">Hours</p>
                <p className="text-xs sm:text-sm text-slate-600">Not weeks for contractor dispatch</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 text-center">
                <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">100%</p>
                <p className="text-xs sm:text-sm text-slate-600">Visibility with GPS tracking</p>
              </div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 mt-4">
              <h3 className="font-bold text-slate-900 mb-2">With AskIsaac, you get:</h3>
              <ul className="space-y-2 text-sm sm:text-base text-slate-700">
                <li className="flex items-center gap-2"><span className="text-emerald-500">✓</span> Expanded supplier network ready to respond fast</li>
                <li className="flex items-center gap-2"><span className="text-emerald-500">✓</span> Credentialed contractors dispatched in minutes</li>
                <li className="flex items-center gap-2"><span className="text-emerald-500">✓</span> Automated competitive bidding with compliance built-in</li>
                <li className="flex items-center gap-2"><span className="text-emerald-500">✓</span> Real-time GPS tracking on every job</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    // Slide 3: The Solution - AskIsaac Model
    {
      id: "solution",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-blue-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-cyan-400 flex-shrink-0" />
            Three Revenue Tiers
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-blue-200 mb-6">
            An on-demand orchestration layer that connects hospitals to their backyard network of pre-vetted suppliers AND credentialed contractors — in minutes, not weeks.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {/* Tier 1 */}
            <div className="bg-emerald-500/20 backdrop-blur rounded-xl p-4 sm:p-6 border border-emerald-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-6 h-6 text-emerald-400" />
                <h3 className="text-base sm:text-lg font-bold text-emerald-400">Tier 1: Managed Compliance</h3>
              </div>
              <p className="text-blue-100 text-xs sm:text-sm mb-3">&quot;Regulatory Shield&quot; for the hospital</p>
              <div className="bg-emerald-500/20 rounded-lg p-3 text-center mb-3">
                <p className="text-xl sm:text-2xl font-bold text-emerald-400">44% / 35%</p>
                <p className="text-xs text-emerald-200">Service / Supply Margin</p>
              </div>
              <p className="text-xs text-blue-200">Execute complex projects (Critical Water Loops) with trusted oversight</p>
            </div>
            {/* Tier 2 */}
            <div className="bg-blue-500/20 backdrop-blur rounded-xl p-4 sm:p-6 border border-blue-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-6 h-6 text-blue-400" />
                <h3 className="text-base sm:text-lg font-bold text-blue-400">Tier 2: Enterprise Marketplace</h3>
              </div>
              <p className="text-blue-100 text-xs sm:text-sm mb-3">Standardizing &quot;Chaotic&quot; procurement</p>
              <div className="bg-blue-500/20 rounded-lg p-3 text-center mb-3">
                <p className="text-xl sm:text-2xl font-bold text-blue-400">8%</p>
                <p className="text-xs text-blue-200">Surcharge per Project</p>
              </div>
              <p className="text-xs text-blue-200">Hospitals use preferred vendors through verified tracking &amp; reporting</p>
            </div>
            {/* Tier 3 */}
            <div className="bg-violet-500/20 backdrop-blur rounded-xl p-4 sm:p-6 border border-violet-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Layers className="w-6 h-6 text-violet-400" />
                <h3 className="text-base sm:text-lg font-bold text-violet-400">Tier 3: White-Label SaaS</h3>
              </div>
              <p className="text-blue-100 text-xs sm:text-sm mb-3">&quot;Keys to the Kingdom&quot; for Internal Teams</p>
              <div className="bg-violet-500/20 rounded-lg p-3 text-center mb-3">
                <p className="text-lg sm:text-xl font-bold text-violet-400">Annual Lease</p>
                <p className="text-xs text-violet-200">+ Per-Project Fees</p>
              </div>
              <p className="text-xs text-blue-200">Permanent digital record for all facility compliance</p>
            </div>
          </div>
          <div className="mt-6 bg-white/5 rounded-xl p-4 border border-cyan-500/30">
            <h3 className="text-cyan-400 font-bold mb-2">How It Works:</h3>
            <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
              <span className="bg-blue-500/30 px-3 py-1 rounded-full">1. Hospital posts need</span>
              <span className="text-blue-400">→</span>
              <span className="bg-blue-500/30 px-3 py-1 rounded-full">2. Isaac pings network</span>
              <span className="text-blue-400">→</span>
              <span className="bg-blue-500/30 px-3 py-1 rounded-full">3. Vendors bid blindly</span>
              <span className="text-blue-400">→</span>
              <span className="bg-emerald-500/30 px-3 py-1 rounded-full">4. GPS-verified fulfillment</span>
            </div>
          </div>
        </div>
      )
    },
    // Slide 4: The Opportunity
    {
      id: "opportunity",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-900 mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
            Market Opportunity
          </h2>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 md:gap-8">
            <div className="bg-blue-50 border border-blue-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
              <h3 className="text-sm sm:text-lg md:text-xl font-bold text-blue-700 mb-2 sm:mb-4">Total Addressable Market</h3>
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-600">$47B</p>
              <p className="text-xs sm:text-sm text-slate-600 mt-2">Healthcare supply chain + maintenance services</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
              <h3 className="text-sm sm:text-lg md:text-xl font-bold text-emerald-700 mb-2 sm:mb-4">Our Target</h3>
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-emerald-600">$2.4B</p>
              <p className="text-xs sm:text-sm text-slate-600 mt-2">Emergency procurement + on-demand maintenance</p>
            </div>
          </div>
          <div className="mt-3 sm:mt-6 md:mt-8 text-center">
            <p className="text-sm sm:text-xl md:text-2xl font-bold text-blue-900">
              Seeking <span className="text-emerald-600">$700,000</span> for <span className="text-emerald-600">20%</span> Equity
            </p>
          </div>
        </div>
      )
    },
    // Slide 3: Company Overview
    {
      id: "overview",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-900 mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
            Company Overview
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            <div className="md:col-span-2 space-y-3 sm:space-y-4 md:space-y-6">
              <p className="text-xs sm:text-sm md:text-lg text-slate-700 leading-relaxed">
                <strong>AskIsaac Inc</strong> is a healthcare technology startup created by George Broughton III, Founder of Sterile Mate LLC. Born from rapid response medical sourcing during COVID-19, we've evolved into the "Uber for Healthcare Operations."
              </p>
              <div className="grid grid-cols-4 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                <div className="bg-blue-50 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 text-center">
                  <p className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-600">2020</p>
                  <p className="text-[10px] sm:text-xs md:text-sm text-slate-600">Founded</p>
                </div>
                <div className="bg-emerald-50 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 text-center">
                  <p className="text-lg sm:text-2xl md:text-3xl font-bold text-emerald-600">$1M+</p>
                  <p className="text-[10px] sm:text-xs md:text-sm text-slate-600">Revenue</p>
                </div>
                <div className="bg-violet-50 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 text-center">
                  <p className="text-lg sm:text-2xl md:text-3xl font-bold text-violet-600">4+</p>
                  <p className="text-[10px] sm:text-xs md:text-sm text-slate-600">Installs</p>
                </div>
                <div className="bg-amber-50 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 text-center">
                  <p className="text-lg sm:text-2xl md:text-3xl font-bold text-amber-600">5</p>
                  <p className="text-[10px] sm:text-xs md:text-sm text-slate-600">Partners</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-900 to-slate-900 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 text-white">
              <h3 className="font-bold text-sm sm:text-base md:text-lg mb-2 sm:mb-4">Key Verticals</h3>
              <ul className="space-y-1 sm:space-y-2 md:space-y-3 text-xs sm:text-sm md:text-base">
                <li className="flex items-center gap-2">
                  <Droplets className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 flex-shrink-0" />
                  <span>Water Purification</span>
                </li>
                <li className="flex items-center gap-2">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400 flex-shrink-0" />
                  <span>Janitorial & EVS</span>
                </li>
                <li className="flex items-center gap-2">
                  <Code className="w-4 h-4 sm:w-5 sm:h-5 text-violet-400 flex-shrink-0" />
                  <span>Healthcare Software</span>
                </li>
                <li className="flex items-center gap-2">
                  <Droplets className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 flex-shrink-0" />
                  <span>Bleu Flux Water</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    // Slide 4: Leadership
    {
      id: "leadership",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-900 mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
            Leadership
          </h2>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 md:gap-8">
            <div className="bg-gradient-to-br from-slate-100 to-blue-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
              <div className="flex items-center gap-2 sm:gap-4 mb-2 sm:mb-4">
                <img src={GEORGE_IMAGE} alt="George Broughton III" className="w-14 h-14 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-lg sm:rounded-xl object-cover flex-shrink-0" />
                <div>
                  <h3 className="text-base sm:text-xl md:text-2xl font-bold">George Broughton III</h3>
                  <p className="text-blue-600 font-semibold text-xs sm:text-sm md:text-base">Founder, AskIsaac Inc</p>
                </div>
              </div>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>Founder of Sterile Mate LLC • Creator of AskIsaac.io</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>Launched water purification solutions division (2022)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>$1M+ in water purification installations</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>Launching Bleu Flux alkaline water brand (2026)</span>
                </li>
              </ul>
            </div>
            <div className="space-y-6">
              <div className="bg-emerald-50 rounded-xl p-5">
                <h4 className="font-bold text-lg mb-2">Amit Bhavasar</h4>
                <p className="text-emerald-700 font-semibold text-sm mb-2">Water Purification Division</p>
                <p className="text-slate-600 text-sm">Operations, water and bottling division leadership</p>
              </div>
              <div className="bg-slate-100 rounded-xl p-5">
                <h4 className="font-bold mb-3">Planned Hires (With Investment)</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>• Sales Representatives (2) — Regional growth</li>
                  <li>• Sales Manager — Distributor expansion</li>
                  <li>• Administrator — Operations support</li>
                  <li>• Customer Service — Order fulfillment</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 5: Strategic Moats
    {
      id: "partnerships",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-6 sm:p-12 overflow-y-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 mb-6 sm:mb-8 flex items-center gap-3">
            <Lock className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600" />
            Strategic Moats
          </h2>
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Moat 1: Institutional Access */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6">
              <h3 className="font-bold text-lg text-blue-800 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                1. Institutional Access Moat (The &quot;Hard&quot; Moat)
              </h3>
              <ul className="space-y-3 text-slate-700 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div><strong className="text-blue-800">6 Years of TMC Trust:</strong> Direct relationships with facility leadership in the world&apos;s largest medical complex.</div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div><strong className="text-blue-800">SDVOSB Federal Gateway:</strong> Fast-Track for nationwide VA facility access.</div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div><strong className="text-blue-800">Tier 1 Partnership:</strong> Live integration with one of the largest U.S. public health systems.</div>
                </li>
              </ul>
            </div>
            
            {/* Moat 2: Technical & Supply */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6">
              <h3 className="font-bold text-lg text-emerald-800 mb-4 flex items-center gap-2">
                <Droplets className="w-5 h-5 text-emerald-600" />
                2. Technical &amp; Supply Moat (The &quot;Capability&quot; Moat)
              </h3>
              <ul className="space-y-3 text-slate-700 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div><strong className="text-emerald-800">Water Purification Solutions:</strong> AAMI ST108 water purification for Sterile Processing &amp; Dialysis.</div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div><strong className="text-emerald-800">Automated Backorder Sourcing:</strong> AskIsaac RFQ engine \u2014 6-year Sterile Mate benchmark, now automated for statewide scale.</div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div><strong className="text-emerald-800">Managed Compliance IP:</strong> Proprietary logic for 44% Managed Mode and 35% Supply Margin.</div>
                </li>
              </ul>
            </div>

            {/* Key Partners */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-2xl p-6">
              <h3 className="font-bold text-lg text-cyan-800 mb-3 flex items-center gap-2">
                <Droplets className="w-5 h-5 text-cyan-600" />
                Sterile Mate Water Solutions
              </h3>
              <p className="text-cyan-600 text-sm font-semibold mb-2">EXCLUSIVE Healthcare Representative</p>
              <ul className="space-y-1 text-slate-700 text-sm">
                <li>• RO/DI water purification systems</li>
                <li>• AAMI ST108 compliance solutions</li>
                <li>• DI tank exchange programs</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-bold text-lg text-amber-800 mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-600" />
                Automated Backorder Sourcing
              </h3>
              <p className="text-amber-600 text-sm font-semibold mb-2">Sterile Mate\u2019s 6-Year Benchmark \u2014 Now Automated</p>
              <ul className="space-y-1 text-slate-700 text-sm">
                <li>\u2022 AskIsaac RFQ engine \u2014 door-opener into every TX hospital system</li>
                <li>\u2022 Automated supplier quotes + ETA tracking</li>
                <li>\u2022 Flagship revenue line: $1.2M Y1 \u2192 $5.0M Y5</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    // Slide 6: Automated Backorder Sourcing — Our Flagship Revenue Engine
    {
      id: "backorder-flagship",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-amber-900 via-orange-900 to-slate-900 text-white p-12">
          <h2 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <Zap className="w-8 h-8 text-amber-400" />
            Automated Backorder Sourcing \u2014 Our Flagship Revenue Engine
          </h2>
          <p className="text-amber-300 mb-8">Sterile Mate\u2019s 6-year benchmark \u00b7 now automated by AskIsaac \u00b7 scaling regionally + statewide</p>
          <div className="flex-1 grid grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-xl">6-Year Track Record</h3>
                  <p className="text-amber-400 text-sm">Door-opener into every TX hospital system</p>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-4">Backorder sourcing has driven the majority of Sterile Mate\u2019s revenue for the last 6 years \u2014 serving Harris Health, Houston Methodist, MD Anderson, Memorial Hermann, VA, and the broader Texas Medical Center.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> 200+ supplier network across Texas</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> Hospital-specific SKU maps + vendor rules</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> Trusted benchmark and carrier account</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> Materials-management shortcut vendor</li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-xl">AskIsaac Automation</h3>
                  <p className="text-orange-400 text-sm">Regional \u2192 statewide scale</p>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-4">AskIsaac digitizes the entire backorder RFQ workflow so we can multiply volume without adding proportional headcount. This is the primary lever behind the Plan C regional and statewide expansion.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400" /> Instant multi-supplier RFQ dispatch</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400" /> Automated ETA + \u201Cnow in stock\u201D alerts</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400" /> Revenue: $1.2M Y1 \u2192 $3.2M Y3 \u2192 $5.0M Y5</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400" /> Cross-sells Water / Devices / Construction</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    // Slide 7: AskIsaac.io - Global Orchestration Exchange
    {
      id: "askisaac",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-2 flex items-center gap-2 sm:gap-3">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-cyan-400 flex-shrink-0" />
            AskIsaac.io — The Safety Valve
          </h2>
          <p className="text-cyan-300 mb-4 sm:mb-6 md:mb-8 text-xs sm:text-sm md:text-base">Global Orchestration Exchange for Healthcare Operations</p>
          
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
            {/* The Challenge */}
            <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
              <h3 className="text-sm sm:text-lg md:text-xl font-bold text-amber-400 mb-2 sm:mb-4">Healthcare Supply Challenges</h3>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                <li className="flex items-start gap-1 sm:gap-2">
                  <span className="text-amber-400 mt-0.5">⚡</span>
                  <span>$500K+ annual downtime per facility</span>
                </li>
                <li className="flex items-start gap-1 sm:gap-2">
                  <span className="text-amber-400 mt-0.5">⚡</span>
                  <span>Supply chain disruptions need backup options</span>
                </li>
                <li className="flex items-start gap-1 sm:gap-2">
                  <span className="text-amber-400 mt-0.5">⚡</span>
                  <span>Manual 3-quote process takes weeks</span>
                </li>
                <li className="flex items-start gap-1 sm:gap-2">
                  <span className="text-amber-400 mt-0.5">⚡</span>
                  <span>Equipment downtime impacts patient care</span>
                </li>
              </ul>
            </div>
            
            {/* The Solution */}
            <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
              <h3 className="text-sm sm:text-lg md:text-xl font-bold text-emerald-400 mb-2 sm:mb-4">AskIsaac Solution</h3>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                <li className="flex items-start gap-1 sm:gap-2">
                  <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>QuickBid Engine — bypass 3-quote delay</span>
                </li>
                <li className="flex items-start gap-1 sm:gap-2">
                  <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Pre-vetted local contractors & suppliers</span>
                </li>
                <li className="flex items-start gap-1 sm:gap-2">
                  <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>AAMI ST108 compliance automation</span>
                </li>
                <li className="flex items-start gap-1 sm:gap-2">
                  <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>$1M+ saved on over-specced systems</span>
                </li>
              </ul>
            </div>
            
            {/* Platform Features */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 md:col-span-2">
              <h3 className="text-sm sm:text-lg font-bold mb-2 sm:mb-3">Platform Capabilities</h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 text-xs sm:text-sm">
                <div className="text-center p-2 sm:p-3 bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-cyan-400">QuickBid</p>
                  <p className="text-slate-400 text-[10px] sm:text-xs">TX compliance bidding</p>
                </div>
                <div className="text-center p-2 sm:p-3 bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-blue-400">GPO Backup</p>
                  <p className="text-slate-400 text-[10px] sm:text-xs">Backorder sourcing</p>
                </div>
                <div className="text-center p-2 sm:p-3 bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-amber-400">Vendor Showcase</p>
                  <p className="text-slate-400 text-[10px] sm:text-xs">Supplier portfolios</p>
                </div>
                <div className="text-center p-2 sm:p-3 bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-emerald-400">Med Courier</p>
                  <p className="text-slate-400 text-[10px] sm:text-xs">STAT delivery</p>
                </div>
                <div className="text-center p-2 sm:p-3 bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-red-400">Fleet Command</p>
                  <p className="text-slate-400 text-[10px] sm:text-xs">Live GPS tracking</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-3 sm:mt-4 md:mt-6 text-center">
            <p className="text-sm sm:text-base md:text-lg font-medium text-cyan-300">
              "When the Big Box power goes out, AskIsaac kicks in to keep your ORs running"
            </p>
          </div>
        </div>
      )
    },
    // Slide 8: Customer Success
    {
      id: "customers",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-12">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 flex items-center gap-3">
            <Hospital className="w-8 h-8 text-blue-600" />
            Customer Success
          </h2>
          <div className="flex-1 grid grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-6 text-white">
              <Award className="w-10 h-10 mb-4" />
              <h3 className="font-bold text-xl mb-2">Major Public Health System</h3>
              <p className="text-blue-100 text-sm mb-4">Live Beta + Enterprise Client</p>
              <p className="font-semibold">3 Water Purification Contracts</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl p-6 text-white">
              <Shield className="w-10 h-10 mb-4" />
              <h3 className="font-bold text-xl mb-2">National Integrated Healthcare</h3>
              <p className="text-emerald-100 text-sm mb-4">Federal Healthcare Partnership</p>
              <p className="font-semibold">Water Purification Systems</p>
            </div>
            <div className="bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl p-6 text-white">
              <Shield className="w-10 h-10 mb-4" />
              <h3 className="font-bold text-xl mb-2">Regional Federal Medical Center</h3>
              <p className="text-violet-100 text-sm mb-4">Federal Healthcare Facility</p>
              <p className="font-semibold">Water Purification Systems</p>
            </div>
            <div className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl p-6 text-white">
              <Hospital className="w-10 h-10 mb-4" />
              <h3 className="font-bold text-xl mb-2">Leading Pediatric Institution</h3>
              <p className="text-rose-100 text-sm mb-4">Pediatric Healthcare</p>
              <p className="font-semibold">Healthcare Solutions</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-6 text-white col-span-2">
              <Award className="w-10 h-10 mb-4" />
              <h3 className="font-bold text-xl mb-2">State of Texas — Awarded Bid</h3>
              <p className="text-amber-100 text-sm mb-4">Government Contract</p>
              <p className="font-semibold">Reverse Osmosis System Installation</p>
            </div>
          </div>
        </div>
      )
    },
    // Slide 8: Financial Projections
    {
      id: "financials",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-12">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-blue-600" />
            Financial Projections
          </h2>
          <div className="flex-1 grid grid-cols-2 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">Revenue Forecast</h3>
              <div className="space-y-4">
                <div className="bg-slate-100 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">2026 (Current)</span>
                    <span className="text-2xl font-bold text-blue-600">$1.5M</span>
                  </div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{width: '18%'}} />
                  </div>
                </div>
                <div className="bg-slate-100 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">2027 (Y1)</span>
                    <span className="text-2xl font-bold text-emerald-600">$4.54M</span>
                  </div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{width: '39%'}} />
                  </div>
                </div>
                <div className="bg-slate-100 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">2028 (Y2)</span>
                    <span className="text-2xl font-bold text-violet-600">$7.59M</span>
                  </div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-violet-500 rounded-full" style={{width: '65%'}} />
                  </div>
                </div>
                <div className="bg-slate-100 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">2029 (Y3)</span>
                    <span className="text-2xl font-bold text-amber-600">$11.64M</span>
                  </div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full" style={{width: '100%'}} />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">2028 Revenue Mix</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-amber-100 rounded-lg">
                  <span>Backorder Sourcing (AskIsaac Flagship)</span>
                  <span className="font-bold text-amber-700">28% ($2.1M)</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-100 rounded-lg">
                  <span>Private-Label Gloves (Intco)</span>
                  <span className="font-bold text-emerald-700">20% ($1.54M)</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-100 rounded-lg">
                  <span>Water Purification</span>
                  <span className="font-bold text-blue-700">18% ($1.4M)</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-cyan-100 rounded-lg">
                  <span>Medical Devices + Construction</span>
                  <span className="font-bold text-cyan-700">26% ($2M)</span>
                </div>
              </div>
              <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-sm text-slate-600 mb-1">2028 Net Operating Income</p>
                <p className="text-3xl font-bold text-emerald-600">$2.37M</p>
                <p className="text-sm text-emerald-700">DSCR 6.71x on SBA 504 stack</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 9: The Deal
    {
      id: "deal",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-12">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <Rocket className="w-8 h-8 text-violet-400" />
            The Deal
          </h2>
          <div className="flex-1 grid grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="text-center bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
                <p className="text-slate-300 mb-2">Seeking</p>
                <p className="text-5xl font-bold text-white mb-2">$700,000</p>
                <p className="text-3xl font-bold text-violet-400">20% Equity</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-sm">Pre-Money</p>
                  <p className="text-xl font-bold">$2.8M</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-sm">Post-Money</p>
                  <p className="text-xl font-bold">$3.5M</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-sm">Minimum</p>
                  <p className="text-xl font-bold">$50,000</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-sm">Target Close</p>
                  <p className="text-xl font-bold">Q1 2026</p>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-bold text-xl mb-4">Use of Funds</h3>
              <div className="space-y-3">
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex justify-between mb-2">
                    <span>Sales & Marketing</span>
                    <span className="font-bold">$280K (40%)</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{width: '40%'}} />
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex justify-between mb-2">
                    <span>Operations Executive</span>
                    <span className="font-bold">$140K (20%)</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{width: '20%'}} />
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex justify-between mb-2">
                    <span>Regional Expansion</span>
                    <span className="font-bold">$140K (20%)</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-violet-500 rounded-full" style={{width: '20%'}} />
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex justify-between mb-2">
                    <span>Marketing & Working Capital</span>
                    <span className="font-bold">$140K (20%)</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full" style={{width: '20%'}} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 10: Exit Strategy
    {
      id: "exit",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-12">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            Exit Strategy
          </h2>
          <div className="flex-1 grid grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-xl mb-2">Strategic Acquisition</h3>
              <p className="text-slate-500 text-sm mb-4">3-5 Years</p>
              <p className="text-slate-700 text-sm mb-4">Healthcare distributors (McKesson, Henry Schein, Medline) actively acquiring specialized providers.</p>
              <div className="pt-4 border-t border-emerald-200">
                <p className="text-sm text-slate-500">Expected Return</p>
                <p className="text-2xl font-bold text-emerald-600">4-6x Revenue</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-violet-500 flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-xl mb-2">PE Rollup</h3>
              <p className="text-slate-500 text-sm mb-4">4-6 Years</p>
              <p className="text-slate-700 text-sm mb-4">Private equity firms actively consolidating healthcare services sector.</p>
              <div className="pt-4 border-t border-violet-200">
                <p className="text-sm text-slate-500">Expected Return</p>
                <p className="text-2xl font-bold text-violet-600">5-8x EBITDA</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-xl mb-2">Dividend Distributions</h3>
              <p className="text-slate-500 text-sm mb-4">Ongoing</p>
              <p className="text-slate-700 text-sm mb-4">Strong margins enable profit distributions while maintaining growth trajectory.</p>
              <div className="pt-4 border-t border-amber-200">
                <p className="text-sm text-slate-500">Expected Yield</p>
                <p className="text-2xl font-bold text-amber-600">8-12% Annual</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 11: Why Now
    {
      id: "whynow",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white p-12">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <Zap className="w-8 h-8" />
            Why Invest Now?
          </h2>
          <div className="flex-1 grid grid-cols-2 gap-6">
            <div className="bg-white/20 backdrop-blur rounded-2xl p-6 border border-white/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-lg">Backorder Sourcing Automated</h3>
              </div>
              <p className="text-white/80">Sterile Mate\u2019s 6-year benchmark revenue engine is now fully automated via AskIsaac \u2014 competitors need 5+ years of hospital relationships to catch up.</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-2xl p-6 border border-white/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Rocket className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-lg">Bleu Flux Q2 2026 Launch</h3>
              </div>
              <p className="text-white/80">Premium alkaline water brand ready — first mover advantage in healthcare hydration market.</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-2xl p-6 border border-white/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Globe className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-lg">Federal Healthcare Expansion</h3>
              </div>
              <p className="text-white/80">Active pipeline with the nation&apos;s largest integrated healthcare system — investment accelerates nationwide rollout.</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-2xl p-6 border border-white/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-lg">Market Timing</h3>
              </div>
              <p className="text-white/80">Hospitals aggressively seeking revenue recovery solutions post-COVID — perfectly positioned.</p>
            </div>
          </div>
          <div className="mt-8 text-center">
            <p className="text-2xl font-bold">Target Close: Q1 2026</p>
          </div>
        </div>
      )
    },
    // Slide 12: Contact
    {
      id: "contact",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-3 sm:p-6 md:p-8">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-4 text-center">Let's Connect</h2>
          <div className="flex-1 flex items-center justify-center min-h-0">
            <div className="text-center">
              <img src={GEORGE_IMAGE} alt="George Broughton III" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-lg sm:rounded-xl object-cover mx-auto mb-2 sm:mb-3 border-2 border-white/20" />
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-0.5 sm:mb-1">George Broughton III</h3>
              <p className="text-blue-300 text-sm sm:text-base md:text-lg mb-2 sm:mb-4">Founder, AskIsaac Inc</p>
              <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm md:text-base">
                <a href="mailto:george.broughton@sterilemate.com" className="flex items-center justify-center gap-1 sm:gap-2 hover:text-blue-300 transition-colors">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="truncate">george.broughton@sterilemate.com</span>
                </a>
                <a href="tel:+18009883892" className="flex items-center justify-center gap-1 sm:gap-2 hover:text-blue-300 transition-colors">
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  800.988.3892
                </a>
                <div className="flex items-center justify-center gap-1 sm:gap-2 text-slate-400 text-xs sm:text-sm">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="hidden sm:inline">396 W Greens Road, Suite 1139, Houston, TX 77067</span>
                  <span className="sm:hidden">Houston, TX 77067</span>
                </div>
              </div>
              <div className="mt-2 sm:mt-4">
                <img src={STERILE_MATE_LOGO} alt="Sterile Mate" className="w-20 sm:w-28 md:w-32 h-auto mx-auto opacity-50" />
              </div>
            </div>
          </div>
          <div className="text-center text-slate-500 text-[10px] sm:text-xs pt-1 sm:pt-2">
            Confidential — For Approved Investors Only — January 2026
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Controls - Hidden when printing */}
      <div className="print:hidden sticky top-0 z-50 bg-slate-800 border-b border-slate-700 px-3 sm:px-6 py-2 sm:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-4">
            <Button
              onClick={prevSlide}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 px-2 sm:px-3"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Previous</span>
            </Button>
            <span className="text-slate-400 text-xs sm:text-sm whitespace-nowrap">
              <span className="sm:hidden">{currentSlide + 1}/{slides.length}</span>
              <span className="hidden sm:inline">Slide {currentSlide + 1} of {slides.length}</span>
            </span>
            <Button
              onClick={nextSlide}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 px-2 sm:px-3"
            >
              <span className="hidden sm:inline mr-1">Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button
            onClick={handleDownload}
            size="sm"
            className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 px-2 sm:px-4"
          >
            <Printer className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Print / Save as PDF</span>
          </Button>
        </div>
      </div>

      {/* Slide Thumbnails - Hidden on mobile and when printing */}
      <div className="print:hidden hidden sm:block bg-slate-800/50 border-b border-slate-700 px-6 py-3 overflow-x-auto">
        <div className="max-w-7xl mx-auto flex gap-2">
          {slides.map((slide, idx) => (
            <button
              key={slide.id}
              onClick={() => setCurrentSlide(idx)}
              className={`flex-shrink-0 w-16 h-10 sm:w-20 sm:h-12 rounded border-2 overflow-hidden transition-all ${
                currentSlide === idx 
                  ? 'border-blue-500 ring-2 ring-blue-500/50' 
                  : 'border-slate-600 hover:border-slate-500'
              }`}
            >
              <div className="w-full h-full bg-slate-700 flex items-center justify-center text-xs text-slate-400">
                {idx + 1}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Slide Dots - Visible only on mobile */}
      <div className="print:hidden sm:hidden bg-slate-800/50 border-b border-slate-700 px-3 py-2 overflow-x-auto">
        <div className="flex justify-center gap-1.5">
          {slides.map((slide, idx) => (
            <button
              key={slide.id}
              onClick={() => setCurrentSlide(idx)}
              className={`flex-shrink-0 w-2.5 h-2.5 rounded-full transition-all ${
                currentSlide === idx 
                  ? 'bg-blue-500 scale-125' 
                  : 'bg-slate-600 hover:bg-slate-500'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Current Slide View - Screen */}
      <div className="print:hidden max-w-6xl mx-auto p-2 sm:p-4 md:p-8">
        <div 
          ref={deckRef}
          className="aspect-[16/9] rounded-lg sm:rounded-2xl overflow-hidden shadow-2xl border border-slate-700"
        >
          {slides[currentSlide].content}
        </div>
      </div>

      {/* Print View - All Slides */}
      <div className="hidden print:block">
        {slides.map((slide, idx) => (
          <div 
            key={slide.id} 
            className="w-full h-screen page-break-after"
            style={{ pageBreakAfter: 'always' }}
          >
            {slide.content}
          </div>
        ))}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: landscape;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .page-break-after {
            page-break-after: always;
          }
        }
      `}</style>
    </div>
  );
}
